import { useEffect, useRef, useState } from 'react';
import ProductList from '../components/product/ProductList';
import SearchControls from '../components/search/SearchControls';
import SEO from '../components/common/SEO';
import publicService from '../services/publicService';
import { formatPrice } from '../utils/helpers';
import './Search.css';

const EMPTY_FILTERS = { sort: '', category: '', minPrice: '', maxPrice: '' };

const Search = () => {
    const [searchQuery, setSearchQuery] = useState('');
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [products, setProducts] = useState([]);
    const [total, setTotal] = useState(0);
    const [facets, setFacets] = useState(null);
    const [mode, setMode] = useState('popular');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const reqIdRef = useRef(0);

    const { sort, category, minPrice, maxPrice } = filters;

    useEffect(() => {
        const reqId = ++reqIdRef.current;
        const trimmed = searchQuery.trim();
        const debounce = setTimeout(async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await publicService.searchProducts({
                    q: trimmed,
                    sort,
                    category,
                    minPrice,
                    maxPrice,
                });
                if (reqIdRef.current !== reqId) return;
                setProducts(data.products || []);
                setTotal(data.total ?? (data.products || []).length);
                setMode(data.mode || (trimmed ? 'search' : 'popular'));
                // Facets describe the whole match set, so they survive a
                // filter change and the panel never empties out mid-refine.
                if (data.facets) setFacets(data.facets);
            } catch (err) {
                if (reqIdRef.current !== reqId) return;
                setError(err?.response?.data?.message || 'Failed to load products.');
                setProducts([]);
                setTotal(0);
            } finally {
                if (reqIdRef.current === reqId) setLoading(false);
            }
        }, trimmed ? 250 : 0);

        return () => clearTimeout(debounce);
    }, [searchQuery, sort, category, minPrice, maxPrice]);

    const trimmed = searchQuery.trim();
    const activeCategory = facets?.categories?.find((item) => item.slug === category);
    const hasFilters = Boolean(category || minPrice || maxPrice);
    const clearFilters = () => setFilters((current) => ({ ...current, ...EMPTY_FILTERS, sort: current.sort }));

    const priceChipLabel = () => {
        if (minPrice && maxPrice) return `${formatPrice(minPrice)} - ${formatPrice(maxPrice)}`;
        if (minPrice) return `From ${formatPrice(minPrice)}`;
        return `Up to ${formatPrice(maxPrice)}`;
    };

    const resultsHeading = () => {
        if (mode === 'popular' && !trimmed) {
            return hasFilters ? `${total} product${total !== 1 ? 's' : ''}` : 'Popular right now';
        }
        return `${total} result${total !== 1 ? 's' : ''} for "${trimmed}"`;
    };

    return (
        <div className="search-page">
            <SEO
                title={trimmed ? `Search Results for "${trimmed}"` : 'Search Products'}
                description={trimmed ? `Searching for ${trimmed} at Essentick. Browse our collection of premium watches.` : 'Search for premium watches and lifestyle products at Essentick.'}
                keywords={`search, watches, Nepal, ${trimmed}, Essentick`}
            />
            <div className="search-header-container">
                <div className="search-input-group">
                    <div className="search-input-wrapper">
                        <svg
                            className="search-icon-large"
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            aria-hidden="true"
                        >
                            <path d="M9 17C13.4183 17 17 13.4183 17 9C17 4.58172 13.4183 1 9 1C4.58172 1 1 4.58172 1 9C1 13.4183 4.58172 17 9 17Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M19 19L14.65 14.65" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <input
                            type="text"
                            className="search-input"
                            placeholder="Search products..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            autoFocus
                        />
                    </div>

                    <SearchControls
                        facets={facets}
                        value={filters}
                        onChange={setFilters}
                        hasQuery={Boolean(trimmed)}
                    />
                </div>

                {hasFilters && (
                    <div className="search-chips">
                        {activeCategory && (
                            <button
                                type="button"
                                className="search-chip"
                                onClick={() => setFilters((current) => ({ ...current, category: '' }))}
                            >
                                {activeCategory.name}
                                <span className="chip-remove" aria-hidden="true">
                                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                        <path d="M3 3L9 9M9 3L3 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                    </svg>
                                </span>
                                <span className="sr-only">Remove category filter</span>
                            </button>
                        )}
                        {(minPrice || maxPrice) && (
                            <button
                                type="button"
                                className="search-chip"
                                onClick={() => setFilters((current) => ({ ...current, minPrice: '', maxPrice: '' }))}
                            >
                                {priceChipLabel()}
                                <span className="chip-remove" aria-hidden="true">
                                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                        <path d="M3 3L9 9M9 3L3 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                    </svg>
                                </span>
                                <span className="sr-only">Remove price filter</span>
                            </button>
                        )}
                        <button type="button" className="search-chip-clear" onClick={clearFilters}>
                            Clear all
                        </button>
                    </div>
                )}
            </div>

            <div className="search-results">
                {error ? (
                    <div className="no-results">
                        <h2>Something went wrong</h2>
                        <p>{error}</p>
                    </div>
                ) : loading && products.length === 0 ? (
                    <ProductList products={[]} loading />
                ) : products.length > 0 ? (
                    <>
                        <div className="search-results-header">
                            <h1>{resultsHeading()}</h1>
                            {total > products.length && (
                                <p className="search-results-note">
                                    Showing the first {products.length}
                                </p>
                            )}
                        </div>
                        {/* Keeping the previous page visible while the next one
                            loads stops the grid collapsing on every keystroke. */}
                        <div className={`products-content${loading ? ' is-refreshing' : ''}`}>
                            <main className="products-main">
                                <ProductList products={products} />
                            </main>
                        </div>
                    </>
                ) : (
                    <div className="no-results">
                        <h2>No products found</h2>
                        {/* Blaming the spelling would be wrong when it is the
                            filters, not the words, that ruled everything out. */}
                        {hasFilters ? (
                            <p>
                                {trimmed
                                    ? `Nothing matches "${trimmed}" within the filters you picked.`
                                    : 'Nothing matches the filters you picked.'}
                            </p>
                        ) : (
                            <>
                                <p>We couldn&apos;t find any matches for &quot;{trimmed}&quot;.</p>
                                <p>Try checking your spelling or using different keywords.</p>
                            </>
                        )}
                        {hasFilters && (
                            <button type="button" className="no-results-action" onClick={clearFilters}>
                                Clear filters
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Search;
